/**
 * Email Admin View
 *
 * Per-agent email configuration. Each agent (static or dynamic) can have
 * its own SMTP/IMAP credentials. An agent picker at the top selects which
 * agent to configure. Settings are saved to the DB per agent_id and
 * hot-reloaded without gateway restart.
 */

import { html, nothing } from "lit";

// --------------- Types ---------------

export type EmailConfigEntry = {
  id: string;
  agentId: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPass: string;
  smtpTlsReject: boolean;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  imapUser: string;
  imapPass: string;
  imapTlsReject: boolean;
  fromAddress: string;
  fromName: string;
  smtpConfigured: boolean;
  imapConfigured: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EmailTestResult = {
  smtp?: { ok: boolean; message: string };
  imap?: { ok: boolean; message: string; unseenCount?: number };
};

export type EmailServiceStatus = {
  running: boolean;
  connected: boolean;
  lastPollAt: string | null;
  lastPollError: string | null;
  messagesReceived: number;
  pollCount: number;
  pollIntervalMs: number;
};

export type EmailAgentOption = {
  id: string;
  name?: string;
};

export type EmailProps = {
  loading: boolean;
  error: string | null;
  saving: boolean;
  config: EmailConfigEntry | null;
  form: Partial<EmailConfigEntry> | null;
  testing: boolean;
  testAddress: string;
  testResult: EmailTestResult | null;
  status: EmailServiceStatus | null;
  /** Available agents for the picker */
  agents: EmailAgentOption[];
  /** Currently selected agent ID */
  selectedAgentId: string | null;
  onSelectAgent: (agentId: string) => void;
  onRefresh: () => void;
  onSave: () => void;
  onDelete: () => void;
  onTest: () => void;
  onFormChange: (field: string, value: unknown) => void;
  onTestAddressChange: (value: string) => void;
};

// --------------- Helpers ---------------

function fieldRow(
  label: string,
  field: string,
  value: string,
  onChange: (field: string, value: unknown) => void,
  opts?: { type?: string; placeholder?: string; wide?: boolean },
) {
  const type = opts?.type ?? "text";
  const placeholder = opts?.placeholder ?? "";
  const wide = opts?.wide ?? false;
  return html`
    <div style="${wide ? "grid-column:1/-1;" : ""}">
      <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">${label}</label>
      <input
        type=${type}
        class="input"
        style="width:100%;box-sizing:border-box;"
        .value=${value}
        placeholder=${placeholder}
        @input=${(e: Event) => onChange(field, (e.target as HTMLInputElement).value)}
      />
    </div>
  `;
}

function numberFieldRow(
  label: string,
  field: string,
  value: number,
  onChange: (field: string, value: unknown) => void,
  opts?: { placeholder?: string },
) {
  const placeholder = opts?.placeholder ?? "";
  return html`
    <div>
      <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">${label}</label>
      <input
        type="number"
        class="input"
        style="width:100%;box-sizing:border-box;"
        .value=${String(value)}
        placeholder=${placeholder}
        @input=${(e: Event) => onChange(field, Number((e.target as HTMLInputElement).value))}
      />
    </div>
  `;
}

function toggleRow(
  label: string,
  field: string,
  value: boolean,
  onChange: (field: string, value: unknown) => void,
  description?: string,
) {
  return html`
    <div>
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
        <input
          type="checkbox"
          .checked=${value}
          @change=${(e: Event) => onChange(field, (e.target as HTMLInputElement).checked)}
        />
        <span style="font-size:0.85em;color:var(--fg-muted);">${label}</span>
      </label>
      ${description ? html`<div style="font-size:0.75em;color:var(--fg-muted);margin-top:2px;margin-left:26px;">${description}</div>` : nothing}
    </div>
  `;
}

function testResultBadge(result: { ok: boolean; message: string }) {
  const color = result.ok ? "var(--green, #22c55e)" : "var(--red, #ef4444)";
  const bg = result.ok ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)";
  const icon = result.ok ? "\u2713" : "\u2717";
  return html`
    <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;border-radius:6px;background:${bg};border:1px solid ${color};">
      <span style="color:${color};font-weight:bold;font-size:1.1em;">${icon}</span>
      <span style="font-size:0.9em;">${result.message}</span>
    </div>
  `;
}

// --------------- Render ---------------

export function renderEmail(props: EmailProps) {
  const form = props.form;
  const hasConfig = props.config !== null;

  return html`
    <div style="display:flex;flex-direction:column;gap:20px;">
      <!-- Agent Picker -->
      <div class="card">
        <div class="card-title">Agent Email Configuration</div>
        <div class="card-sub">Each agent can have its own email account. Select an agent to configure its email settings.</div>
        <div style="margin-top:16px;display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
          <div style="flex:1;min-width:220px;max-width:400px;">
            <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Agent</label>
            <select
              class="input"
              style="width:100%;box-sizing:border-box;padding:8px 12px;"
              @change=${(e: Event) => props.onSelectAgent((e.target as HTMLSelectElement).value)}
            >
              ${
                props.agents.length === 0
                  ? html`
                      <option value="" disabled selected>No agents available</option>
                    `
                  : nothing
              }
              ${props.agents.map(
                (agent) => html`
                  <option value=${agent.id} ?selected=${agent.id === props.selectedAgentId}>
                    ${agent.name ? `${agent.name} (${agent.id})` : agent.id}
                  </option>
                `,
              )}
            </select>
          </div>
          <div style="display:flex;gap:8px;padding-top:20px;">
            <button class="btn" @click=${props.onRefresh} ?disabled=${props.loading}>
              ${props.loading ? "Loading..." : "Refresh"}
            </button>
            <button
              class="btn primary"
              @click=${props.onSave}
              ?disabled=${props.saving || props.loading || !form || !props.selectedAgentId}
            >
              ${props.saving ? "Saving..." : "Save"}
            </button>
            ${
              hasConfig
                ? html`
                  <button
                    class="btn"
                    style="color:var(--red, #ef4444);border-color:var(--red, #ef4444);"
                    @click=${props.onDelete}
                    ?disabled=${props.saving || props.loading}
                  >
                    Delete
                  </button>
                `
                : nothing
            }
          </div>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        !props.selectedAgentId
          ? html`
              <div class="callout info">Select an agent above to configure its email settings.</div>
            `
          : nothing
      }

      ${
        props.selectedAgentId && props.loading && !form
          ? html`<div class="callout info">Loading email configuration for ${props.selectedAgentId}...</div>`
          : nothing
      }

      ${
        props.selectedAgentId && !props.loading && !hasConfig && !form
          ? html`<div class="callout info">No email configured for agent "${props.selectedAgentId}" yet. Fill in the settings below and click Save to create one.</div>`
          : nothing
      }

      <!-- IMAP Polling Service Status -->
      ${
        props.status && props.selectedAgentId
          ? html`
            <div class="card">
              <div class="card-title">IMAP Polling Service</div>
              <div class="card-sub">Runtime status of the background IMAP inbox polling for this agent.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:16px;">
                <div>
                  <div style="font-size:0.75em;color:var(--fg-muted);margin-bottom:4px;">Service</div>
                  <div style="display:flex;align-items:center;gap:6px;">
                    <span style="width:8px;height:8px;border-radius:50%;background:${props.status.running ? "var(--green, #22c55e)" : "var(--fg-muted, #666)"};display:inline-block;"></span>
                    <span style="font-weight:600;">${props.status.running ? "Running" : "Stopped"}</span>
                  </div>
                </div>
                <div>
                  <div style="font-size:0.75em;color:var(--fg-muted);margin-bottom:4px;">Connected</div>
                  <span style="font-weight:600;">${props.status.connected ? "Yes" : "No"}</span>
                </div>
                <div>
                  <div style="font-size:0.75em;color:var(--fg-muted);margin-bottom:4px;">Poll Count</div>
                  <span style="font-weight:600;">${props.status.pollCount}</span>
                </div>
                <div>
                  <div style="font-size:0.75em;color:var(--fg-muted);margin-bottom:4px;">Messages Received</div>
                  <span style="font-weight:600;">${props.status.messagesReceived}</span>
                </div>
                <div>
                  <div style="font-size:0.75em;color:var(--fg-muted);margin-bottom:4px;">Polling Interval</div>
                  <span style="font-weight:600;">${props.status.pollIntervalMs > 0 ? props.status.pollIntervalMs / 1000 + "s" : "\u2014"}</span>
                </div>
                <div>
                  <div style="font-size:0.75em;color:var(--fg-muted);margin-bottom:4px;">Last Poll</div>
                  <span style="font-size:0.9em;">${props.status.lastPollAt ? new Date(props.status.lastPollAt).toLocaleString() : "Never"}</span>
                </div>
              </div>
              ${
                props.status.lastPollError
                  ? html`
                    <div style="margin-top:12px;padding:8px 12px;border-radius:6px;background:rgba(239, 68, 68, 0.08);border:1px solid var(--red, #ef4444);font-size:0.85em;">
                      <span style="color:var(--red, #ef4444);font-weight:bold;">Last Error:</span> ${props.status.lastPollError}
                    </div>
                  `
                  : nothing
              }
            </div>
          `
          : nothing
      }

      ${
        props.selectedAgentId
          ? html`
            <!-- SMTP Settings (Outbound) -->
            <div class="card">
              <div class="card-title">SMTP Settings (Outbound)</div>
              <div class="card-sub">Configure the SMTP server for sending emails from this agent.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                ${fieldRow("SMTP Host", "smtpHost", form?.smtpHost ?? "", props.onFormChange, {
                  placeholder: "smtp.gmail.com",
                  wide: true,
                })}
                ${numberFieldRow(
                  "SMTP Port",
                  "smtpPort",
                  form?.smtpPort ?? 587,
                  props.onFormChange,
                  {
                    placeholder: "587",
                  },
                )}
                ${toggleRow(
                  "Use TLS/SSL",
                  "smtpSecure",
                  form?.smtpSecure ?? false,
                  props.onFormChange,
                  "Enable for port 465. STARTTLS is used automatically for port 587.",
                )}
                ${toggleRow(
                  "Reject Unauthorized TLS",
                  "smtpTlsReject",
                  form?.smtpTlsReject ?? true,
                  props.onFormChange,
                  "Disable to allow self-signed certificates. Not recommended for production.",
                )}
                ${fieldRow("Username", "smtpUser", form?.smtpUser ?? "", props.onFormChange, {
                  placeholder: "bot@example.com",
                })}
                ${fieldRow("Password", "smtpPass", form?.smtpPass ?? "", props.onFormChange, {
                  type: "password",
                  placeholder: "App password or SMTP password",
                })}
              </div>
              <div style="margin-top:16px;border-top:1px solid var(--border, #333);padding-top:16px;">
                <div style="font-size:0.85em;font-weight:600;color:var(--fg-muted);margin-bottom:12px;">Sender Identity</div>
                <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                  ${fieldRow(
                    "From Address",
                    "fromAddress",
                    form?.fromAddress ?? "",
                    props.onFormChange,
                    {
                      type: "email",
                      placeholder: "bot@example.com",
                    },
                  )}
                  ${fieldRow("From Name", "fromName", form?.fromName ?? "", props.onFormChange, {
                    placeholder: "i-GlooBot",
                  })}
                </div>
              </div>
            </div>

            <!-- IMAP Settings (Inbound) -->
            <div class="card">
              <div class="card-title">IMAP Settings (Inbound)</div>
              <div class="card-sub">Configure the IMAP server for receiving emails for this agent.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                ${fieldRow("IMAP Host", "imapHost", form?.imapHost ?? "", props.onFormChange, {
                  placeholder: "imap.gmail.com",
                  wide: true,
                })}
                ${numberFieldRow(
                  "IMAP Port",
                  "imapPort",
                  form?.imapPort ?? 993,
                  props.onFormChange,
                  {
                    placeholder: "993",
                  },
                )}
                ${toggleRow(
                  "Use TLS/SSL",
                  "imapSecure",
                  form?.imapSecure ?? true,
                  props.onFormChange,
                  "Most IMAP servers use TLS on port 993.",
                )}
                ${toggleRow(
                  "Reject Unauthorized TLS",
                  "imapTlsReject",
                  form?.imapTlsReject ?? true,
                  props.onFormChange,
                  "Disable to allow self-signed certificates. Not recommended for production.",
                )}
                ${fieldRow("Username", "imapUser", form?.imapUser ?? "", props.onFormChange, {
                  placeholder: "bot@example.com",
                })}
                ${fieldRow("Password", "imapPass", form?.imapPass ?? "", props.onFormChange, {
                  type: "password",
                  placeholder: "App password or IMAP password",
                })}
              </div>
            </div>

            <!-- Test Connection -->
            <div class="card">
              <div class="card-title">Test Connection</div>
              <div class="card-sub">Verify SMTP and IMAP connectivity for this agent. SMTP test sends a real email.</div>
              <div style="margin-top:16px;display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;">
                <div style="flex:1;min-width:220px;">
                  <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Test Email Address</label>
                  <input
                    type="email"
                    class="input"
                    style="width:100%;box-sizing:border-box;"
                    .value=${props.testAddress}
                    placeholder="your@email.com"
                    @input=${(e: Event) => props.onTestAddressChange((e.target as HTMLInputElement).value)}
                  />
                </div>
                <button
                  class="btn primary"
                  @click=${props.onTest}
                  ?disabled=${props.testing || props.loading || !hasConfig}
                  style="white-space:nowrap;"
                >
                  ${props.testing ? "Testing..." : "Run Test"}
                </button>
              </div>
              ${
                !hasConfig
                  ? html`
                      <div style="margin-top: 8px; font-size: 0.8em; color: var(--fg-muted)">
                        Save the configuration first before testing.
                      </div>
                    `
                  : nothing
              }

              ${
                props.testResult
                  ? html`
                    <div style="margin-top:16px;display:flex;flex-direction:column;gap:8px;">
                      ${
                        props.testResult.smtp
                          ? html`
                            <div>
                              <div style="font-size:0.8em;font-weight:600;color:var(--fg-muted);margin-bottom:4px;">SMTP</div>
                              ${testResultBadge(props.testResult.smtp)}
                            </div>
                          `
                          : nothing
                      }
                      ${
                        props.testResult.imap
                          ? html`
                            <div>
                              <div style="font-size:0.8em;font-weight:600;color:var(--fg-muted);margin-bottom:4px;">IMAP</div>
                              ${testResultBadge(props.testResult.imap)}
                            </div>
                          `
                          : nothing
                      }
                    </div>
                  `
                  : nothing
              }
            </div>

            <!-- Record Info -->
            ${
              props.config
                ? html`
                  <div class="card" style="opacity:0.7;">
                    <div class="card-title" style="font-size:0.9em;">Record Info</div>
                    <div style="margin-top:8px;display:flex;gap:24px;font-size:0.85em;color:var(--fg-muted);">
                      <span>Agent: ${props.config.agentId}</span>
                      <span>Created: ${new Date(props.config.createdAt).toLocaleString()}</span>
                      <span>Updated: ${new Date(props.config.updatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                `
                : nothing
            }
          `
          : nothing
      }
    </div>
  `;
}
